import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, Save, Loader2, Download, Upload } from "lucide-react";
import { GRADE_GROUPS } from "@/lib/subjects";

const SECTIONS = [
  { key: "paragraf", title: "PARAGRAF", labelCol: "DENEME" },
  { key: "deyim_atasozu", title: "DEYİM & ATASÖZÜ", labelCol: "ÜNİTE" },
  { key: "ingilizce_kelime", title: "İNGİLİZCE KELİME EZBER", labelCol: "ÜNİTE" },
  { key: "turkce_sozluk", title: "TÜRKÇE SÖZLÜK", labelCol: "KONU" },
  { key: "kitap", title: "KİTAP", labelCol: "İSİM" },
];

export default function AdminBulkEval() {
  const [params] = useSearchParams();
  const [grade, setGrade] = useState(Number(params.get("grade")) || 3);
  const [students, setStudents] = useState([]);
  const [month, setMonth] = useState("2026-09");
  const [section, setSection] = useState("paragraf");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [label, setLabel] = useState("");
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get("/students", { params: { grade } }).then(({ data }) => setStudents(data));
  }, [grade]);

  const set = (sid, field, val) =>
    setRows((prev) => ({ ...prev, [sid]: { ...prev[sid], [field]: val } }));

  const filledCount = Object.values(rows).filter((r) => r?.dogru || r?.yanlis || r?.bos || r?.approved).length;

  const downloadTemplate = () => {
    const csv = ["kullaniciadi,ad,dogru,yanlis,bos", ...students.map((s) => `${s.username || ""},${s.name},,,`)].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `toplu_${grade}sinif_${section}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!label.trim()) { toast.error("Önce Deneme/ünite adı girin"); e.target.value = ""; return; }
    const fd = new FormData();
    fd.append("file", f); fd.append("month", month); fd.append("section", section); fd.append("date", date); fd.append("label", label.trim());
    try {
      const { data } = await api.post("/monthly-eval/bulk-file", fd);
      toast.success(`${data.saved} öğrenci Excel'den içe aktarıldı ✓`);
      setReport(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { e.target.value = ""; }
  };

  const save = async () => {
    if (!label.trim()) return toast.error("Deneme/ünite adı gerekli");
    const payload = Object.entries(rows)
      .map(([student_id, r]) => ({
        student_id,
        dogru: Number(r.dogru) || 0,
        yanlis: Number(r.yanlis) || 0,
        bos: Number(r.bos) || 0,
        approved: !!r.approved,
      }))
      .filter((r) => r.dogru || r.yanlis || r.bos || r.approved);
    if (!payload.length) return toast.error("En az bir öğrenci için değer girin");
    setSaving(true);
    try {
      const { data } = await api.post("/monthly-eval/bulk", { month, section, date, label: label.trim(), rows: payload });
      toast.success(`${data.saved} öğrencinin ${SECTIONS.find((s) => s.key === section).title} kaydı eklendi ✓`);
      setRows({});
      setLabel("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const secLabel = SECTIONS.find((s) => s.key === section)?.labelCol;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600"><Users className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Sınıf Geneli Toplu Giriş</h1>
            <p className="text-slate-500">Aylık değerlendirmeyi tüm sınıf için tek ekrandan girin.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="bulk-template-btn" onClick={downloadTemplate} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 font-semibold text-slate-700 shadow-sm transition hover:shadow-md active:scale-95">
            <Download className="h-5 w-5" /> Şablon İndir
          </button>
          <label data-testid="bulk-upload-label" className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 font-semibold text-slate-700 shadow-sm transition hover:shadow-md active:scale-95">
            <Upload className="h-5 w-5" /> Excel/CSV Yükle
            <input data-testid="bulk-file-input" type="file" accept=".csv,.xlsx,.xls" onChange={uploadFile} className="hidden" />
          </label>
          <button data-testid="bulk-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700 active:scale-95 disabled:opacity-60">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Kaydet ({filledCount})
          </button>
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-2 lg:grid-cols-5">
        <div>
          <label className={lbl}>Sınıf</label>
          <select data-testid="bulk-grade-select" value={grade} onChange={(e) => { setGrade(Number(e.target.value)); setRows({}); }} className={inp}>
            {GRADE_GROUPS.map((grp) => (
              <optgroup key={grp.label} label={grp.label}>
                {grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label className={lbl}>Ay</label>
          <input data-testid="bulk-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inp} />
        </div>
        <div>
          <label className={lbl}>Bölüm</label>
          <select data-testid="bulk-section-select" value={section} onChange={(e) => setSection(e.target.value)} className={inp}>
            {SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Tarih</label>
          <input data-testid="bulk-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inp} />
        </div>
        <div>
          <label className={lbl}>{secLabel} Adı</label>
          <input data-testid="bulk-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ör. 5. DENEME" className={inp} />
        </div>
      </div>

      {report && (report.skipped_count > 0 ? (
        <div data-testid="import-report" className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="mb-2 font-display text-lg font-bold text-amber-800">İçe Aktarım Raporu — {report.saved} kaydedildi, {report.skipped_count} atlandı</div>
          <div className="max-h-52 overflow-y-auto rounded-xl border border-amber-200 bg-white">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-amber-100 text-[11px] uppercase text-amber-600"><th className="px-3 py-2 text-left">Satır</th><th className="px-3 py-2 text-left">Değer</th><th className="px-3 py-2 text-left">Neden</th></tr></thead>
              <tbody className="divide-y divide-amber-50">
                {report.skipped.map((s, i) => (
                  <tr key={i} data-testid={`skip-row-${i}`}><td className="px-3 py-1.5 font-mono text-xs text-slate-500">{s.line}</td><td className="px-3 py-1.5 font-semibold text-slate-800">{s.value || "—"}</td><td className="px-3 py-1.5 text-rose-600">{s.reason}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div data-testid="import-report" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 font-semibold text-emerald-800">İçe Aktarım Raporu: {report.saved} öğrenci başarıyla aktarıldı, atlanan satır yok ✓</div>
      ))}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-[#1E293B] px-4 py-2.5 font-display text-sm font-bold uppercase tracking-widest text-white">
          {grade}. Sınıf — {students.length} öğrenci
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-[11px] font-bold uppercase text-slate-500">
              <th className="px-4 py-2.5 text-left">Öğrenci</th>
              <th className="w-24 px-2 py-2.5 text-center">Doğru</th>
              <th className="w-24 px-2 py-2.5 text-center">Yanlış</th>
              <th className="w-24 px-2 py-2.5 text-center">Boş</th>
              <th className="w-24 px-2 py-2.5 text-center">Onaylı</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {students.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Bu sınıfta öğrenci yok.</td></tr>
            ) : students.map((s) => {
              const r = rows[s.id] || {};
              const filled = r.dogru || r.yanlis || r.bos || r.approved;
              return (
                <tr key={s.id} data-testid={`bulk-row-${s.id}`} className={`transition ${filled ? "bg-indigo-50/50" : "hover:bg-slate-50"}`}>
                  <td className="px-4 py-2 font-semibold text-slate-800">{s.name}{s.section ? <span className="ml-1 text-xs text-slate-400">/{s.section}</span> : ""}</td>
                  <td className="px-2 py-2 text-center"><input data-testid={`bulk-d-${s.id}`} type="number" min={0} value={r.dogru ?? ""} onChange={(e) => set(s.id, "dogru", e.target.value)} className={`${cellInp} focus:border-emerald-400`} /></td>
                  <td className="px-2 py-2 text-center"><input data-testid={`bulk-y-${s.id}`} type="number" min={0} value={r.yanlis ?? ""} onChange={(e) => set(s.id, "yanlis", e.target.value)} className={`${cellInp} focus:border-rose-400`} /></td>
                  <td className="px-2 py-2 text-center"><input data-testid={`bulk-b-${s.id}`} type="number" min={0} value={r.bos ?? ""} onChange={(e) => set(s.id, "bos", e.target.value)} className={cellInp} /></td>
                  <td className="px-2 py-2 text-center">
                    <input data-testid={`bulk-ap-${s.id}`} type="checkbox" checked={!!r.approved} onChange={(e) => set(s.id, "approved", e.target.checked)} className="h-5 w-5 rounded accent-emerald-600" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">Yalnızca değer girilen veya onaylanan öğrenciler kaydedilir. Net = Doğru − Yanlış/3 otomatik hesaplanır.</p>
    </div>
  );
}

const lbl = "mb-1 block text-sm font-semibold text-slate-700";
const inp = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";
const cellInp = "w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-center font-semibold outline-none";
